import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import api from '../../utils/apiClient';

/**
 * Image uploader component with preview
 * @param {Object} props - Component props
 * @param {string} props.imageUrl - Current image URL
 * @param {Function} props.onImageUploaded - Callback when image is uploaded
 * @param {string} props.uploadEndpoint - API endpoint for upload
 * @param {string} props.label - Label for the uploader
 * @param {string} props.className - Additional CSS classes
 * @param {number} props.maxSizeMB - Maximum file size in MB
 * @param {string} props.aspectRatio - Aspect ratio for the preview (e.g., "1/1", "16/9")
 * @param {boolean} props.disabled - Whether the uploader is disabled
 */
const ImageUploader = ({
  imageUrl,
  onImageUploaded,
  uploadEndpoint,
  label = 'Upload Image',
  className = '',
  maxSizeMB = 20,
  aspectRatio = 'auto',
  disabled = false
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(imageUrl);
  const [error, setError] = useState(null);

  // Handle file selection
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Clear any previous errors
    setError(null);

    // Validate file size
    if (file.size > maxSizeMB * 1024 * 1024) {
      const errorMsg = `File size should be less than ${maxSizeMB}MB`;
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      const errorMsg = 'Please upload an image file (JPG, PNG, GIF, etc.)';
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    // Create a preview
    const reader = new FileReader();
    reader.onload = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload the file
    try {
      setIsUploading(true);

      const formData = new FormData();
      formData.append('image', file);

      const response = await api.upload(uploadEndpoint, formData);

      // The api client already returns response.data, so we don't need to access .data again
      if (response && response.success) {
        toast.success('Image uploaded successfully');
        onImageUploaded(response.imageUrl);
      } else {
        throw new Error(response?.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      const errorMsg = 'Failed to upload image. Please try again.';
      setError(errorMsg);
      toast.error(errorMsg);
      // Revert to original image if upload fails
      setPreviewUrl(imageUrl);
    } finally {
      setIsUploading(false);
    }
  };

  // Handle image removal
  const handleRemoveImage = () => {
    setPreviewUrl('');
    onImageUploaded('');
  };

  return (
    <div className={`w-full ${className}`}>
      <label className="block text-sm font-medium text-gray-400 mb-1">{label}</label>

      {previewUrl ? (
        // Show image preview with remove button
        <div className="relative" style={{ aspectRatio }}>
          <img
            src={previewUrl}
            alt="Preview"
            className="w-full h-full object-cover rounded-lg"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/logo.png";
            }}
          />
          {!disabled && (
            <button
              type="button"
              onClick={handleRemoveImage}
              className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700 transition-colors"
              title="Remove image"
            >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
            </button>
          )}
        </div>
      ) : (
        // Show upload area
        <div className={`border-2 border-dashed border-gray-600 rounded-lg p-4 text-center ${!disabled ? 'hover:border-gray-500' : 'opacity-70'} transition-colors`}>
          <label className={`block w-full h-full ${!disabled ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
              disabled={isUploading || disabled}
            />
            <div className="text-gray-400 py-4">
              {isUploading ? (
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-2"></div>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                  <polyline points="21 15 16 10 5 21"></polyline>
                </svg>
              )}
              <p>
                {isUploading
                  ? 'Uploading...'
                  : disabled
                    ? 'Image upload disabled'
                    : 'Click to upload an image'
                }
              </p>
              {!disabled && (
                <>
                  <p className="text-xs mt-1">Max size: {maxSizeMB}MB</p>
                  <p className="text-xs mt-1 text-gray-500">Recommended formats: JPG, PNG, GIF</p>
                  {error && <p className="text-xs mt-1 text-red-500">{error}</p>}
                </>
              )}
            </div>
          </label>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
